import { z } from "zod";
import { ConfiguredState } from "./control";
import { procedureSchema } from "./auditSession";
import { OptroWorkpaperFile } from "./optroSync";

// OpsAudit: the (optional) grouping entity for Work Steps. Grouping is
// visual-only — landing page and bulk import. See CONTEXT.md.
export const OpsAuditSchema = z.object({
  id: z.string(),
  name: z.string(),
  externalRef: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type OpsAudit = z.infer<typeof OpsAuditSchema>;

// Structured Optro linkage object attached to work-step list/detail
// responses. Mirrors `ExternalRefSchema` (src/models/audit/externalControl.ts)
// with the datum-id field renamed for the ops-audit-item source. Unlike
// controls' compound `source::...` ref, the work-step ref format is a plain
// `WS#<id>` display string.
export const WorkStepExternalRefSchema = z.object({
  id: z.string().uuid(),
  externalRef: z.string(),
  externalOpsAuditItemId: z.number().nullable().optional(),
  createdAt: z.string(),
});

export type WorkStepExternalRef = z.infer<typeof WorkStepExternalRefSchema>;

export const DryWorkStepSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  createdBy: z.string().nullable(),
  name: z.string(),
  description: z.string(),
  // AuditBoard section/subsection names carried as plain text labels (no
  // section structure model in Midship).
  section: z.string().nullable(),
  subsection: z.string().nullable(),
  preparer: z.string().nullable(),
  configuredState: z
    .nativeEnum(ConfiguredState)
    .default(ConfiguredState.NotConfigured),
  // Optional in Midship, unlike AuditBoard where membership is required.
  opsAuditId: z.string().nullable(),
  // Only present on work steps linked to an external system; tolerate both
  // a missing key and an explicit null.
  externalRef: WorkStepExternalRefSchema.nullable().optional(),
  // Session clones (is_template=false / duplicatedFrom) share this shape, so
  // both keys are optional here.
  isTemplate: z.boolean().optional(),
  duplicatedFrom: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type DryWorkStep = z.infer<typeof DryWorkStepSchema>;

export const WorkStepWithUserSchema = DryWorkStepSchema.extend({
  user: z
    .object({
      id: z.string(),
      email: z.string(),
      createdAt: z.string(),
    })
    .optional()
    .nullable(),
});

export type WorkStepWithUser = z.infer<typeof WorkStepWithUserSchema>;

export const WorkStepFilterOptionsSchema = z.object({
  sections: z.array(z.string()),
  subsections: z.array(z.string()),
  preparers: z.array(z.string()),
  configuredStates: z.array(z.string()),
  // Defaulted so the backend can stop embedding the full audits list here;
  // the Audit filter is fed from useOpsAudits instead.
  opsAudits: z
    .array(z.object({ id: z.string(), name: z.string() }))
    .default([]),
});

export type WorkStepFilterOptions = z.infer<typeof WorkStepFilterOptionsSchema>;

// Mirrors ControlAttributeSchema — the testable statements on a Work Step.
export const WorkStepAttributeSchema = z.object({
  id: z.string(),
  reference: z.string(),
  statement: z.string(),
  description: z.string(),
  attributeOrder: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type WorkStepAttribute = z.infer<typeof WorkStepAttributeSchema>;

// GET /work-steps/work-step/{id} returns the work step's own fields plus its
// attributes and procedures (and, unlike the list endpoint, still no `user`
// key — the `.optional().nullable()` above absorbs that either way). Work
// Steps reuse the existing Procedure model as-is — the endpoint returns fully
// hydrated procedures (columns, requiredDocuments, attributes), so this reads
// the full `procedureSchema` from auditSession.ts rather than a summary shape.
export const WorkStepWithAttributesSchema = WorkStepWithUserSchema.extend({
  attributes: z.array(WorkStepAttributeSchema).default([]),
  procedures: z.array(procedureSchema).default([]),
});

export type WorkStepWithAttributes = z.infer<
  typeof WorkStepWithAttributesSchema
>;

// A work step as listed by the Optro/AuditBoard opsaudit module (mirrors
// OptroControlSummary for the controls import).
export const OptroWorkStepSummarySchema = z.object({
  externalRef: z.string(),
  // The AuditBoard ops_audit_item's numeric id — threaded through the import
  // request's `ops_audit_item_id` so the backend can re-fetch the source row.
  opsAuditItemId: z.number(),
  name: z.string(),
  description: z.string(),
  // Null when the item's parent audit couldn't be resolved from the source
  // (deleted audit, or beyond the audits-lookup page cap).
  opsAuditRef: z.string().nullable(),
  opsAuditName: z.string().nullable(),
  section: z.string().nullable(),
  subsection: z.string().nullable(),
  preparer: z.string().nullable(),
  // Already imported into Midship
  linked: z.boolean(),
  // Queued or processing in an Optro import someone in the workspace
  // started. Listed disabled with an "Importing" badge. Absent from older
  // backends, so it defaults to false.
  importing: z.boolean().default(false),
  // The Optro lifecycle status when it makes the item untestable
  // (Reviewed/Complete: strategy frozen by Optro's edit deny; Not In Scope:
  // never tested). Null for testable items. Rows with a value render
  // disabled with the status as a badge, like `linked` renders "Imported".
  untestableStatus: z.string().nullable(),
});

export type OptroWorkStepSummary = z.infer<typeof OptroWorkStepSummarySchema>;

// `GET /work-steps/external/work-steps` — server-paginated listing envelope,
// mirrors `ListOptroControlsResponseSchema` for the controls import.
export const ListOptroWorkStepsResponseSchema = z.object({
  workSteps: z.array(OptroWorkStepSummarySchema),
  source: z.string().optional(),
  total: z.number(),
  hasMore: z.boolean(),
  nextOffset: z.number(),
});

export type ListOptroWorkStepsResponse = z.infer<
  typeof ListOptroWorkStepsResponseSchema
>;

// `GET /work-steps/external/work-steps/search` — one bucket per selected
// audit: its first matches (bounded server-side) plus the audit's full match
// count. Matches beyond the bucket are reachable through the per-audit
// listing with the same term (the "show all matches" path).
export const OptroWorkStepSearchBucketSchema = z.object({
  opsAuditId: z.number(),
  total: z.number(),
  workSteps: z.array(OptroWorkStepSummarySchema),
});

export type OptroWorkStepSearchBucket = z.infer<
  typeof OptroWorkStepSearchBucketSchema
>;

export const SearchOptroWorkStepsResponseSchema = z.object({
  groups: z.array(OptroWorkStepSearchBucketSchema),
  source: z.string().optional(),
});

// One row in the import wizard's audit-selection step. The backend lists
// Active, non-template ops_audits only, so no status field travels here —
// every row is Active by construction.
export const OptroOpsAuditSummarySchema = z.object({
  id: z.number(),
  // Display-form external ref ("OP#<uid or id>").
  ref: z.string(),
  name: z.string(),
  category: z.string().nullable(),
  period: z.string().nullable(),
  lead: z.string().nullable(),
  // DateOnly strings ("YYYY-MM-DD"); null when the audit has no dates set.
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  // From Optro's persisted `totals` aggregate; null when absent/malformed.
  workStepCount: z.number().nullable(),
});

export type OptroOpsAuditSummary = z.infer<typeof OptroOpsAuditSummarySchema>;

// `GET /work-steps/external/audits` — server-paginated listing envelope for
// the audit-selection step, same cursor contract as the work-steps listing.
export const ListOptroOpsAuditsResponseSchema = z.object({
  opsAudits: z.array(OptroOpsAuditSummarySchema),
  source: z.string().optional(),
  total: z.number(),
  hasMore: z.boolean(),
  nextOffset: z.number(),
});

// How the import job sources the documents it reads to configure one work
// step. `all_optro` leaves it to the backend to list and download every Optro
// workpaper on the item itself, plus any uploads made in the review dialog on
// top; `explicit` sends exactly the files checked there. The files are
// `OptroWorkpaperFile`s — the same type the controls wizard carries — because
// both wizards pick from the same candidate shape and fall back to the same
// presigned upload. `source` is what decides whether a file is submitted as a
// picked Optro attachment or as an upload.
export type WorkStepWorkpaperDecision =
  | { mode: "all_optro"; uploads: OptroWorkpaperFile[] }
  | { mode: "explicit"; files: OptroWorkpaperFile[] };

// What a work step imports with when the review step never resolved its
// row — its Optro files had not been fetched (or the fetch failed) by the
// time Import was pressed. The backend resolves the files instead.
export const DEFAULT_WORKPAPER_DECISION: WorkStepWorkpaperDecision = {
  mode: "all_optro",
  uploads: [],
};

// What the wizard hands to the import job for each selected work step.
export interface WorkStepOptroImportStartItem {
  workStep: OptroWorkStepSummary;
  decision: WorkStepWorkpaperDecision;
}

// A work step's testing session — shown as a "Test" in the UI, like control
// sessions.
export const WorkStepSessionSchema = z.object({
  id: z.string(),
  workStepId: z.string().nullable(),
  userId: z.string().nullable(),
  workspaceId: z.string(),
  name: z.string(),
  wpTemplateId: z.string().nullable().optional(),
  status: z.enum(["in_progress", "in_review", "complete"]),
  createdAt: z.string(),
  updatedAt: z.string(),
  user: z
    .object({ id: z.string(), email: z.string(), createdAt: z.string() })
    .optional()
    .nullable(),
});

export type WorkStepSession = z.infer<typeof WorkStepSessionSchema>;

// The refreshed mirrored fields for a linked work step, as returned by
// `POST /work-steps/external/work-step/{workStepId}/resync`. Optro is the
// source of truth for section/subsection/preparer, so those come back
// nullable (a stale value clears when Optro no longer has one); name and
// description are always populated (see `ResyncWorkStepResponse` docstring).
export const ResyncWorkStepFromOptroResponseSchema = z.object({
  name: z.string(),
  description: z.string(),
  section: z.string().nullable(),
  subsection: z.string().nullable(),
  preparer: z.string().nullable(),
});

export type ResyncWorkStepFromOptroResponse = z.infer<
  typeof ResyncWorkStepFromOptroResponseSchema
>;
